/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';
import isMobile from 'ismobilejs';
import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import type { Theme as LocalTheme } from '@trinity/chat-components/types/theme';
import { useThemedStyles } from '../Theme';
import { useChatInstanceAuthor } from './hooks/useChatInstanceAuthor';
import { UserAuthor } from './Message.Author';
import { authorSpacing, extendedMessageWidth, messageStyles } from './Message.styles';

type Props = {
  classes?: {
    body?: SerializedStyles;
    root?: SerializedStyles;
  };
  value: ReactNode;
};

export const UserMessage = ({
  classes = {
    body: undefined,
    root: undefined,
  },
  value,
}: Props) => {
  const device = isMobile();
  const styles = useStyles();
  const { showAuthor } = useAvatarPanel();

  const author = useChatInstanceAuthor();

  return (
    <div css={css(styles.root, classes.root)}>
      <div
        css={css(
          styles.body,
          classes.body,
          shouldExpandWidth(value) ? css({ maxWidth: extendedMessageWidth }) : undefined
        )}
      >
        {value}
      </div>
      {showAuthor && !device.phone && (
        <div css={styles.authorWrap}>
          <UserAuthor author={author} css={styles.author} />
        </div>
      )}
    </div>
  );
};

const authorWrapCss = css([
  messageStyles.authorWrap,
  {
    marginLeft: authorSpacing,
  },
]);

const authorCss = css(messageStyles.author);

const rootCss = css([
  messageStyles.root,
  {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  },
]);

const bodyCss = (theme: LocalTheme.Theme) =>
  css([
    messageStyles.body,
    {
      backgroundColor: theme.palette.white.main,
      border: `2px solid ${theme.palette.gray.light2}`,
    },
  ]);

const useStyles = () =>
  useThemedStyles({
    author: authorCss,
    authorWrap: authorWrapCss,
    body: bodyCss,
    root: rootCss,
  });

function shouldExpandWidth(value: Props['value']) {
  if (typeof value !== 'string') {
    return false;
  }
  return value.length > 200;
}
